import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Paper,
  Divider,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import CustomCard from '../../../Shared/Components/CustomCard/CustomCard';
import {
  formatInvoiceCashDocumentAmount,
  getInvoiceCashDocumentTypeColor,
  getInvoiceCashDocumentTypeLabel,
  InvoiceCashDocumentType,
} from '../../Service/cashDocumentsLookupMapper';
import {
  useGetInvoiceCashDocumentsByInvoiceLookupQuery,
  useLazyGetInvoiceCashDocumentPdfQuery,
} from '../../Service/cashDocumentsLookupApi';
import type { InvoiceCashDocumentsSectionProps } from './InvoiceCashDocumentsSection.types';
import { invoiceLogger } from '../../utils/invoiceLogger';

/**
 * Invoice Cash Documents Section Component
 *
 * Displays linked KP/KW documents in invoice details/edit page
 * Shows documents in payments section with type chip, amount, date, PDF download
 * Compact display suitable for invoice context (no delete, no full cards)
 */
const InvoiceCashDocumentsSection: React.FC<InvoiceCashDocumentsSectionProps> = ({
  invoiceId,
  compact = true,
  showCreateButton = true,
}) => {
  const navigate = useNavigate();
  const [triggerGetCashDocumentPdf] = useLazyGetInvoiceCashDocumentPdfQuery();

  const {
    data: documents = [],
    isLoading,
    error,
  } = useGetInvoiceCashDocumentsByInvoiceLookupQuery(invoiceId);

  const handleCreate = () => {
    navigate(`/cash-documents/create?invoiceId=${invoiceId}`);
  };

  const handleDownloadPdf = async (id: number, documentNumber: string) => {
    try {
      const pdfBlob = await triggerGetCashDocumentPdf(id).unwrap();

      if (!(pdfBlob instanceof Blob) || pdfBlob.size === 0) {
        throw new Error('Downloaded cash document PDF is empty.');
      }

      const url = window.URL.createObjectURL(pdfBlob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `${documentNumber}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      invoiceLogger.error('PDF download failed:', err);
      alert('Nie udało się pobrać PDF. Spróbuj ponownie.');
    }
  };

  const handleViewDetails = (id: number) => {
    navigate(`/cash-documents/${id}`);
  };

  if (isLoading) {
    return (
      <CustomCard title="Dokumenty kasowe (KP/KW)" icon={<ReceiptIcon color="primary" />}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      </CustomCard>
    );
  }

  if (error) {
    return (
      <CustomCard title="Dokumenty kasowe (KP/KW)" icon={<ReceiptIcon color="primary" />}>
        <Alert severity="error">Nie udało się załadować dokumentów kasowych.</Alert>
      </CustomCard>
    );
  }

  // Don't show section if no documents and compact mode
  if (!documents.length && compact) {
    return null;
  }

  return (
    <CustomCard title="Dokumenty kasowe (KP/KW)" icon={<ReceiptIcon color="primary" />}>
      <Stack spacing={2}>
        {/* Empty State */}
        {!documents.length && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Brak dokumentów kasowych dla tej faktury
            </Typography>
            {showCreateButton && (
              <Tooltip title="Dodaj dokument kasowy">
                <IconButton color="primary" onClick={handleCreate}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        {/* Documents List */}
        {documents.length > 0 && (
          <>
            {documents.map((doc, index) => (
              <Box key={doc.id}>
                {index > 0 && <Divider />}
                <Paper
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:hover': {
                      bgcolor: 'action.hover',
                      cursor: 'pointer',
                    },
                  }}
                  onClick={() => handleViewDetails(doc.id)}
                >
                  {/* Type Chip */}
                  <Chip
                    label={getInvoiceCashDocumentTypeLabel(doc.type)}
                    color={getInvoiceCashDocumentTypeColor(doc.type)}
                    size="small"
                    sx={{ minWidth: 80 }}
                  />

                  {/* Document Info */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {doc.documentNumber}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {doc.documentDate ? dayjs(doc.documentDate).format('DD.MM.YYYY') : '-'}
                      {doc.personName && ` • ${doc.personName}`}
                    </Typography>
                  </Box>

                  {/* Amount */}
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    color={doc.type === InvoiceCashDocumentType.KP ? 'success.main' : 'error.main'}
                    sx={{ minWidth: 100, textAlign: 'right' }}
                  >
                    {formatInvoiceCashDocumentAmount(doc.amount, doc.currency)}
                  </Typography>

                  {/* Actions */}
                  <Box onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Pobierz PDF">
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadPdf(doc.id, doc.documentNumber)}
                        color="primary"
                      >
                        <PictureAsPdfIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              </Box>
            ))}

            {/* Create Button (bottom) */}
            {showCreateButton && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                <Tooltip title="Dodaj dokument kasowy">
                  <IconButton color="primary" onClick={handleCreate}>
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </>
        )}
      </Stack>
    </CustomCard>
  );
};

export default InvoiceCashDocumentsSection;
